import React, { memo, ReactElement, useCallback } from 'react';
import {
  Control,
  FieldPathByValue,
  FieldValues,
  PathValue,
  useController,
  useWatch,
} from 'react-hook-form';

import ComboboxInput, { ComboboxInputProps } from './combobox-input';

export type FormComboboxInputProps<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  control: Control<TFieldValues>;
  controlled?: boolean;
  customValue?: (value?: PathValue<TFieldValues, TPath>) => string | undefined;
  defaultValue?: PathValue<TFieldValues, TPath>;
  name: TPath;
  onBlur?: () => void;
} & Omit<ComboboxInputProps, 'defaultValue' | 'onBlur' | 'onChange' | 'value'>;

const FormComboboxInput = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  control,
  defaultValue,
  name,
  customValue,
  onBlur,
  ...props
}: FormComboboxInputProps<TFieldValues, TPath>): null | ReactElement => {
  const { field, fieldState } = useController({
    control,
    defaultValue,
    name,
  });
  const latestValue = useWatch({
    control,
    name,
    defaultValue,
  });
  const handleChange = useCallback(
    (value: string) => {
      if (customValue) {
        return;
      }

      field.onChange(value);
    },
    [customValue, field],
  );

  const handleBlur = useCallback(() => {
    field?.onBlur();
    onBlur?.();
  }, [field, onBlur]);
  return (
    <ComboboxInput
      {...props}
      errorMessage={fieldState.error?.message ?? fieldState.error?.type}
      name={field.name}
      onBlur={handleBlur}
      onChange={handleChange}
      ref={field.ref}
      value={customValue ? (customValue(latestValue) ?? '') : latestValue || ''}
    />
  );
};

export default memo(FormComboboxInput) as typeof FormComboboxInput;
